const LOCAL_STORAGE_KEY = 'current_samples';

class SampleSelector extends HTMLElement {
  connectedCallback() {
    // Elements
    this.sampleButtons = Array.from(this.querySelectorAll('button[data-sample-id]'));

    // Translations + Settings
    this.addSampleLabel = this.getAttribute('data-add-sample-label');
    this.genericAddErrorLabel = this.getAttribute('data-generic-add-error-label');
    this.tooManySamplesAddErrorLabel = this.getAttribute('data-too-many-samples-add-error-label');
    this.maxProductSamples = parseInt(this.getAttribute('data-max-product-samples')) || 5;

    // Event listeners
    this.unsubscribers = [
      subscribe('sample:add', this.handleSampleAddedEvent.bind(this)),
      subscribe('sample:remove', this.handleSampleRemovedEvent.bind(this)),
    ];

    this.initializeSampleButtons();
  }

  disconnectedCallback() {
    this.unsubscribers.forEach((unsubscriber) => unsubscriber());
  }

  /**
   * Initialize sample buttons. Set checked state + add click listeners.
   */
  initializeSampleButtons() {
    const { success, samples } = this.getCurrentSamples();
    if (!success) return;

    this.sampleButtons.forEach((button) => {
      const sampleId = button.dataset.sampleId;
      if (samples.some((sample) => sample.id === sampleId)) {
        button.setAttribute('aria-pressed', 'true');
      } else if (samples.length >= this.maxProductSamples) {
        button.setAttribute('disabled', 'true');
      }
      button.addEventListener('click', this.handleSampleClick.bind(this));
    });
  }

  /**
   * Get the current samples from localStorage.
   * @returns {Object} - An object with a success flag and the samples array.
   */
  getCurrentSamples() {
    try {
      return {
        success: true,
        samples: JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [],
      };
    } catch (error) {
      console.error('Error retrieving samples', error);
      return { success: false, samples: [] };
    }
  }

  /**
   * Handle the sample click event. Add or remove the sample from localStorage + update attributes for clicked button.
   * @param {Event} event - The event object.
   */
  handleSampleClick(event) {
    const button = event.currentTarget;
    const sampleId = button.dataset.sampleId;
    if (!sampleId) return;
    const image = button.querySelector('img');
    const sampleTitle = button.dataset.sampleTitle;
    const sampleImageSrc = image.src;
    const sampleImageSrcSet = image.srcset;

    const sampleImageAlt = button.dataset.sampleImageAlt;

    const operation = button.getAttribute('aria-pressed') === 'true' ? 'remove' : 'add';
    if (operation === 'add') {
      const result = this.addSample({
        id: sampleId,
        title: sampleTitle,
        imageSrc: sampleImageSrc,
        imageSrcSet: sampleImageSrcSet,
        imageAlt: sampleImageAlt,
      });
      if (!result) return;
      button.setAttribute('aria-pressed', 'true');
      publish('sample:add', { samples: result });
    } else {
      const result = this.removeSample(sampleId);
      if (!result) return;
      button.setAttribute('aria-pressed', 'false');
      publish('sample:remove', { id: sampleId, samples: result });
    }
  }

  /**
   * Add a sample to localStorage.
   * @param {Object} sampleData - The sample data.
   * @returns {array|boolean} - The sample array if the sample was added, false otherwise.
   */
  addSample(sampleData) {
    const { success, samples } = this.getCurrentSamples();
    if (!success) return false;

    if (samples.length >= this.maxProductSamples) {
      window.toast.error(
        this.tooManySamplesAddErrorLabel ||
          'You have reached your maximum number of samples. Please remove a sample before adding another one.'
      );
      return false;
    }

    if (samples.some((sample) => sample.id === sampleData.id)) {
      // If a sample is tried to be added again, we cap it at max 1 and return our previous samples array.
      return samples;
    }

    samples.push({
      id: sampleData.id,
      title: sampleData.title,
      imageSrc: sampleData.imageSrc,
      imageSrcSet: sampleData.imageSrcSet,
      imageAlt: sampleData.imageAlt,
    });
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(samples));
    return samples;
  }

  /**
   * Remove a sample from localStorage.
   * @param {string} idToRemove - The ID of the sample to remove.
   * @returns {array|boolean} - The samples array if the sample was removed, false otherwise.
   */
  removeSample(idToRemove) {
    const { success, samples } = this.getCurrentSamples();
    if (!success) return false;

    const newSamples = samples.filter((sample) => sample.id !== idToRemove);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newSamples));
    return newSamples;
  }

  /**
   * Handle the sample added event.
   * @param {Object} data - The data object.
   */
  handleSampleAddedEvent(data) {
    const { samples } = data;
    if (samples.length < this.maxProductSamples) return;

    const selectedIds = samples.map((sample) => sample.id);

    this.sampleButtons
      .filter((button) => !selectedIds.includes(button.dataset.sampleId))
      .map((button) => button.setAttribute('disabled', 'true'));
  }

  /**
   * Handle the sample removed event.
   * @param {Object} data - The data object.
   */
  handleSampleRemovedEvent(data) {
    const { id } = data;
    this.sampleButtons.forEach((button) => {
      if (button.dataset.sampleId === id) {
        button.setAttribute('aria-pressed', 'false');
      }
      button.removeAttribute('disabled');
    });
  }
}

customElements.define('sample-blocks-container', SampleSelector);

export { LOCAL_STORAGE_KEY };
